import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Empty,
    Row,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    ArrowLeftOutlined,
    DownloadOutlined,
    RobotOutlined,
} from '@ant-design/icons';
import AreaMap from '../components/Map/AreaMap';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import {
    statusColorMap,
    type MapPath,
    type MissionCommandItem,
    type MissionItem,
    type MissionPointItem,
} from '../components/Operations/missionModels';
import { getMissionPointMarkerLabel } from '../components/Operations/missionPointLabels';
import '../style/Operations.css';

const { Text, Title } = Typography;

const formatValidationErrors = (value: unknown): string | null => {
    if (!Array.isArray(value) || value.length === 0) {
        return null;
    }

    const messages = value
        .flatMap((item) => {
            if (!item || typeof item !== 'object') return [];
            const candidate = item as {
                constraints?: Record<string, string> | null;
                children?: unknown[];
            };
            const constraintMessages = candidate.constraints ? Object.values(candidate.constraints) : [];
            const childMessages = formatValidationErrors(candidate.children);
            return [
                ...constraintMessages,
                ...(childMessages ? [childMessages] : []),
            ];
        })
        .map((item) => String(item).trim())
        .filter(Boolean);

    return messages.length > 0 ? messages.join(' ') : null;
};

const getErrorMessage = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error)) {
        const responseData = error.response?.data;
        if (typeof responseData === 'string' && responseData.trim()) {
            const trimmed = responseData.trim();
            if (/^<!doctype html/i.test(trimmed) || /<html[\s>]/i.test(trimmed)) {
                return fallback;
            }
            return trimmed;
        }

        const validationMessage = formatValidationErrors(responseData);
        if (validationMessage) return validationMessage;

        return (
            (responseData as any)?.message ||
            (responseData as any)?.error ||
            error.message ||
            fallback
        );
    }

    if (error instanceof Error) return error.message;
    return fallback;
};

const formatTimestamp = (value?: string | null) => {
    if (!value) return <Text type="secondary">Not set</Text>;
    const parsed = dayjs(value);
    if (!parsed.isValid()) return <Text type="secondary">Unknown</Text>;
    return <Text title={parsed.toISOString()}>{parsed.format('DD MMM YYYY HH:mm')}</Text>;
};

const parsePointGeometry = (geom: any): { lat: number; lng: number } | null => {
    if (!geom?.coordinates || !Array.isArray(geom.coordinates) || geom.coordinates.length < 2) {
        return null;
    }
    const lng = Number(geom.coordinates[0]);
    const lat = Number(geom.coordinates[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const areaLabel = (mission: MissionItem) => (
    mission.areaId
        ? mission.area?.name || `Area #${mission.areaId}`
        : mission.externalSource === 'external_api'
            ? 'Area required'
            : 'Unassigned scan'
);

const backTargetFromQuery = (search: string) => {
    const params = new URLSearchParams(search);
    const from = params.get('from');
    const deviceId = params.get('deviceId');
    const operationId = params.get('operationId');

    if (from === 'device' && deviceId) {
        return { label: 'Back to live view', path: `/operations/device/${deviceId}` };
    }
    if (from === 'operation' && operationId) {
        return { label: 'Back to operation', path: `/operations/${operationId}` };
    }
    return { label: 'Back to Operations', path: '/operations' };
};

const MissionDetailView: React.FC = () => {
    const { missionId } = useParams<{ missionId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [mission, setMission] = React.useState<MissionItem | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [markerStatus, setMarkerStatus] = React.useState<string | null>(null);
    const [markerLoading, setMarkerLoading] = React.useState(false);

    const numericMissionId = React.useMemo(() => {
        const parsed = Number(missionId);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }, [missionId]);

    const backTarget = React.useMemo(() => backTargetFromQuery(location.search), [location.search]);

    const loadMission = React.useCallback(async () => {
        if (!numericMissionId) return;
        setLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.MISSION_DETAIL(numericMissionId), {
                withCredentials: true,
            });
            setMission(response.data?.data || response.data || null);
        } catch (error) {
            console.error('Failed to load mission detail', error);
            message.error(getErrorMessage(error, 'Failed to load mission detail'));
            setMission(null);
        } finally {
            setLoading(false);
        }
    }, [numericMissionId]);

    React.useEffect(() => {
        void loadMission();
    }, [loadMission]);

    React.useEffect(() => {
        if (!mission || mission.missionType !== 'slam_scan' || !mission.areaId) {
            setMarkerStatus(null);
            return;
        }

        let cancelled = false;
        setMarkerLoading(true);
        axios.get(ENDPOINTS.AREA_INDOOR_MARKERS(mission.areaId), { withCredentials: true })
            .then((res) => {
                if (cancelled) return;
                const markers = Array.isArray(res.data?.data) ? res.data.data : [];
                setMarkerStatus(markers[0]?.status || null);
            })
            .catch(() => {
                if (!cancelled) setMarkerStatus(null);
            })
            .finally(() => {
                if (!cancelled) setMarkerLoading(false);
            });

        return () => { cancelled = true; };
    }, [mission]);

    const mapPoints = React.useMemo(() => (
        (mission?.missionPoints || [])
            .map((point, index) => ({
                id: point.id,
                lat: point.resolvedLocation?.lat ?? NaN,
                lng: point.resolvedLocation?.lng ?? NaN,
                label: getMissionPointMarkerLabel(mission?.missionPoints || [], point, index),
            }))
            .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng))
    ), [mission]);

    const mapPaths = React.useMemo<MapPath[]>(() => (
        (mission?.missionLegs || [])
            .map((leg) => ({
                id: leg.id,
                selected: leg.status === 'generated' || leg.status === 'radius_fallback',
                warning: leg.status === 'radius_fallback',
                coordinates: Array.isArray(leg.pathGeom?.coordinates)
                    ? leg.pathGeom.coordinates
                        .map((coordinate: any) => ({
                            lng: Number(coordinate?.[0]),
                            lat: Number(coordinate?.[1]),
                        }))
                        .filter((coordinate: any) => Number.isFinite(coordinate.lng) && Number.isFinite(coordinate.lat))
                    : [],
            }))
            .filter((path) => path.coordinates.length >= 2)
    ), [mission]);

    const deviceMarker = React.useMemo(() => {
        const locationPoint = parsePointGeometry(mission?.device?.lastLocation);
        if (!locationPoint) return [];
        return [{
            id: mission?.device?.id || 'device',
            lat: locationPoint.lat,
            lng: locationPoint.lng,
            label: mission?.device?.label || 'Robot',
            selected: true,
        }];
    }, [mission]);

    const downloadAnchorMarker = React.useCallback(async () => {
        if (!mission) return;
        setMarkerLoading(true);
        try {
            await axios.post(ENDPOINTS.MISSION_INDOOR_MARKERS_GENERATE(mission.id), {}, { withCredentials: true });
            setMarkerStatus('printed');
            const pdfRes = await axios.get(
                ENDPOINTS.MISSION_INDOOR_MARKERS_SHEET(mission.id),
                { withCredentials: true, responseType: 'blob' }
            );
            const disposition = pdfRes.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/);
            const fileName = match?.[1] || `oscar-anchor-marker-mission${mission.id}.pdf`;
            const url = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            message.success('Anchor marker PDF downloaded');
        } catch (error) {
            message.error(getErrorMessage(error, 'Failed to download anchor marker'));
        } finally {
            setMarkerLoading(false);
        }
    }, [mission]);

    const exportArduPilot = React.useCallback(async (format: 'qgc_plan' | 'qgc_wpl') => {
        if (!numericMissionId) return;
        try {
            const response = await axios.get(
                ENDPOINTS.MISSION_EXPORT_ARDUPILOT(numericMissionId, format),
                { withCredentials: true, responseType: 'blob' }
            );
            const disposition = response.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/);
            const fileName = match?.[1] || (format === 'qgc_wpl' ? 'mission.waypoints' : 'mission.plan');
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            message.success(`Exported as ${fileName}`);
        } catch (error) {
            message.error(getErrorMessage(error, 'Export failed'));
        }
    }, [numericMissionId]);

    const pointColumns = React.useMemo<ColumnsType<MissionPointItem>>(() => [
        { title: '#', dataIndex: 'pointIndex', key: 'pointIndex', width: 72 },
        {
            title: 'Label',
            dataIndex: 'label',
            key: 'label',
            render: (value: string | null | undefined, point) => value || `Step ${point.pointIndex}`,
        },
        {
            title: 'Action',
            dataIndex: 'actionType',
            key: 'actionType',
            render: (value: string) => <Tag>{value}</Tag>,
        },
        {
            title: 'Source',
            key: 'source',
            render: (_, point) => point.patternPointId ? (
                <Tag color="blue">Pattern #{point.patternPoint?.pointIndex ?? point.patternPointId}</Tag>
            ) : (
                <Tag>Custom</Tag>
            ),
        },
        {
            title: 'Coordinates',
            key: 'coordinates',
            render: (_, point) => point.resolvedLocation ? (
                <Text>{point.resolvedLocation.lat.toFixed(6)}, {point.resolvedLocation.lng.toFixed(6)}</Text>
            ) : (
                <Text type="secondary">No GPS</Text>
            ),
        },
    ], []);

    const commandColumns = React.useMemo<ColumnsType<MissionCommandItem>>(() => [
        { title: '#', dataIndex: 'seq', key: 'seq', width: 72 },
        {
            title: 'Point',
            key: 'missionPointIndex',
            width: 92,
            render: (_, command) => command.missionPointIndex ? (
                <Tag color="blue">P{command.missionPointIndex}</Tag>
            ) : (
                <Text type="secondary">Global</Text>
            ),
        },
        {
            title: 'Kind',
            dataIndex: 'kind',
            key: 'kind',
            width: 110,
            render: (value: string) => <Tag>{value}</Tag>,
        },
        {
            title: 'Command',
            key: 'commandName',
            render: (_, command) => (
                <Space direction="vertical" size={2}>
                    <Text strong>{command.commandName}</Text>
                    {command.label ? <Text type="secondary">{command.label}</Text> : null}
                </Space>
            ),
        },
        {
            title: 'Code / Frame',
            key: 'metadata',
            width: 160,
            render: (_, command) => (
                <Space direction="vertical" size={2}>
                    <Text>{command.commandCode ?? '-'}</Text>
                    <Text type="secondary">Frame {command.frame ?? '-'}</Text>
                </Space>
            ),
        },
        {
            title: 'Source',
            key: 'source',
            width: 120,
            render: (_, command) => <Tag>{command.source || 'manual'}</Tag>,
        },
    ], []);

    if (!numericMissionId) {
        return (
            <div className="operations-page">
                <Alert type="error" showIcon message="Invalid mission ID" />
            </div>
        );
    }

    return (
        <div className="operations-page">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    className="operations-back-button"
                    onClick={() => navigate(backTarget.path)}
                >
                    {backTarget.label}
                </Button>

                <Spin spinning={loading || markerLoading}>
                    {!mission ? (
                        loading ? null : <Empty description="Mission not found" />
                    ) : (
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            {mission.externalSource === 'external_api' && !mission.areaId ? (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message="Imported mission needs setup"
                                    description="Select an area, add START and END points, and review the route in the Mission Editor before this draft can be scheduled or run."
                                />
                            ) : null}
                            <Row justify="space-between" align="top" gutter={[16, 16]}>
                                <Col flex="auto">
                                    <Space direction="vertical" size={4}>
                                        <Space wrap align="center">
                                            <RobotOutlined style={{ fontSize: 22, color: '#1677ff' }} />
                                            <Title level={3} style={{ margin: 0 }}>{mission.name}</Title>
                                            <Tag color={statusColorMap[mission.status] || 'default'}>{mission.status}</Tag>
                                            {mission.missionType ? <Tag>{mission.missionType}</Tag> : null}
                                        </Space>
                                        <Text type="secondary">
                                            {mission.description || `${areaLabel(mission)} mission details`}
                                        </Text>
                                    </Space>
                                </Col>
                                <Col>
                                    {mission.missionType === 'slam_scan' ? (
                                        <Button
                                            icon={<DownloadOutlined />}
                                            loading={markerLoading}
                                            onClick={() => void downloadAnchorMarker()}
                                        >
                                            Marker
                                        </Button>
                                    ) : (
                                        <Button icon={<DownloadOutlined />} onClick={() => void exportArduPilot('qgc_plan')}>
                                            Export
                                        </Button>
                                    )}
                                </Col>
                            </Row>

                            <Space wrap>
                                {mission.device?.currentStatus ? (
                                    <Tag color={statusColorMap[mission.device.currentStatus] || 'default'}>
                                        Robot {mission.device.currentStatus}
                                    </Tag>
                                ) : null}
                                <Tag>{mission.device?.label || 'Unknown robot'}</Tag>
                                <Tag>{areaLabel(mission)}</Tag>
                                {mission.pattern?.name ? <Tag>{mission.pattern.name}</Tag> : <Tag>No pattern</Tag>}
                                {mission.missionType === 'slam_scan' ? (
                                    markerStatus ? <Tag color="orange">Marker {markerStatus}</Tag> : <Tag>Marker not generated</Tag>
                                ) : null}
                            </Space>

                            <Descriptions size="small" column={{ xs: 1, md: 2, xl: 3 }} bordered>
                                <Descriptions.Item label="Mission">{mission.name}</Descriptions.Item>
                                <Descriptions.Item label="Robot">{mission.device?.label || 'Unknown'}</Descriptions.Item>
                                <Descriptions.Item label="Field">{areaLabel(mission)}</Descriptions.Item>
                                <Descriptions.Item label="Scheduled start">{formatTimestamp(mission.scheduledStartAt)}</Descriptions.Item>
                                <Descriptions.Item label="Navigation">
                                    {mission.navigationMode === 'waypoint_goals' ? 'Waypoint pose goals' : 'Planned route'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Turn radius">{mission.routeConfig?.turnRadiusMeters ?? 3} m</Descriptions.Item>
                                <Descriptions.Item label="Last heartbeat">{formatTimestamp(mission.device?.lastHeartbeat)}</Descriptions.Item>
                            </Descriptions>

                            <Row gutter={[12, 12]}>
                                <Col xs={12} md={8}>
                                    <Card size="small">
                                        <Statistic title="Points" value={mission.routeSummary?.pointCount ?? 0} />
                                    </Card>
                                </Col>
                                <Col xs={12} md={8}>
                                    <Card size="small">
                                        <Statistic title="Commands" value={mission.missionCommands?.length ?? 0} />
                                    </Card>
                                </Col>
                                <Col xs={12} md={8}>
                                    <Card size="small">
                                        <Statistic title="Distance (km)" value={mission.routeSummary?.totalDistanceKm ?? 0} precision={2} />
                                    </Card>
                                </Col>
                            </Row>

                            <Card className="operations-map-card" size="small" styles={{ body: { padding: 12 } }}>
                                <div className="operations-map-wrapper operations-detail-modal-map">
                                    <AreaMap
                                        areaGeometry={mission.area?.geom}
                                        areaName={areaLabel(mission)}
                                        points={mapPoints}
                                        paths={mapPaths}
                                        measurements={deviceMarker}
                                    />
                                </div>
                            </Card>

                            {mission.missionType === 'slam_scan' ? (
                                <Card size="small" title="Indoor anchor marker (AprilTag)">
                                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                        <Space wrap align="center">
                                            <Text type="secondary">Marker status:</Text>
                                            {markerLoading ? (
                                                <Tag>Checking...</Tag>
                                            ) : markerStatus === 'confirmed' ? (
                                                <Tag color="green">Confirmed</Tag>
                                            ) : markerStatus === 'detected' ? (
                                                <Tag color="blue">Detected by robot</Tag>
                                            ) : markerStatus === 'printed' ? (
                                                <Tag color="orange">Printed - awaiting scan</Tag>
                                            ) : (
                                                <Tag>Not yet generated</Tag>
                                            )}
                                        </Space>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            This marker is used as a fixed reference point during the SLAM scan.
                                        </Text>
                                    </Space>
                                </Card>
                            ) : null}

                            <Table<MissionPointItem>
                                rowKey="id"
                                className="operations-table"
                                columns={pointColumns}
                                dataSource={mission.missionPoints || []}
                                pagination={false}
                                size="small"
                                scroll={{ x: 720 }}
                            />

                            <Table<MissionCommandItem>
                                rowKey="id"
                                className="operations-table"
                                columns={commandColumns}
                                dataSource={mission.missionCommands || []}
                                pagination={false}
                                size="small"
                                scroll={{ x: 760 }}
                            />
                        </Space>
                    )}
                </Spin>
            </Space>
        </div>
    );
};

export default MissionDetailView;
